'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
// CRITICAL: This import enables OAuth listener for SSR/Next.js apps
// Without this, the OAuth code exchange won't work!
import 'aws-amplify/auth/enable-oauth-listener';
import { configureAmplify } from '@/components/AmplifyConfig';
import { useUserStore } from '@/hooks/useUserStore';

// Ensure Amplify is configured
configureAmplify();

interface AuthUser {
    userId: string;
    username: string;
}

type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextType {
    user: AuthUser | null;
    authState: AuthState;
    isLoading: boolean;
    isAuthenticated: boolean;
    checkAuth: () => Promise<void>;
    refreshToken: () => Promise<string | null>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if this is an OAuth callback (has code parameter in URL)
function isOAuthCallback(): boolean {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') && urlParams.has('state');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [authState, setAuthState] = useState<AuthState>('loading');
    const authCheckComplete = useRef(false);
    const checkingAuth = useRef(false);
    const authGeneration = useRef(0);
    const isOAuthFlow = useRef(false);
    const hasRedirected = useRef(false);
    const tokenRefreshInterval = useRef<NodeJS.Timeout | null>(null);

    // Set OAuth flag on mount if we're in OAuth callback
    useEffect(() => {
        if (isOAuthCallback()) {
            console.log('AuthContext: OAuth callback detected');
            isOAuthFlow.current = true;
        }
    }, []);

    // Sync token to useUserStore
    const syncTokenToStore = useCallback(async (accessTokenStr: string, userId: string, username: string, generation: number) => {
        const session = await fetchAuthSession();
        if (generation !== authGeneration.current) return;
        const claims = session.tokens?.idToken?.payload;
        await useUserStore.getState().login(session.tokens?.idToken?.toString() || accessTokenStr, {
            userId: userId,
            name: typeof claims?.name === 'string' ? claims.name : username,
            email: typeof claims?.email === 'string' ? claims.email : undefined,
        });
        console.log('AuthContext: Synced token to useUserStore');
    }, []);

    // Refresh token and return the new access token
    const refreshToken = useCallback(async (): Promise<string | null> => {
        const generation = authGeneration.current;
        try {
            console.log('AuthContext: Refreshing token...');
            // forceRefresh: true forces Amplify to get a new token
            const session = await fetchAuthSession({ forceRefresh: true });

            if (session?.tokens?.accessToken) {
                const accessTokenStr = session.tokens.accessToken.toString();
                const currentUser = await getCurrentUser();

                if (generation !== authGeneration.current) return null;
                await syncTokenToStore(accessTokenStr, currentUser.userId, currentUser.username, generation);
                console.log('AuthContext: Token refreshed successfully');
                return accessTokenStr;
            }
            return null;
        } catch (error) {
            if (generation !== authGeneration.current) return null;
            authGeneration.current++;
            useUserStore.getState().logout();
            console.error('AuthContext: Token refresh failed', error);
            // If refresh fails, user needs to re-authenticate
            setUser(null);
            setAuthState('unauthenticated');
            return null;
        }
    }, [syncTokenToStore]);

    const checkAuth = useCallback(async () => {
        // Prevent concurrent auth checks
        if (checkingAuth.current) {
            console.log('AuthContext: Auth check already in progress, skipping');
            return;
        }

        checkingAuth.current = true;
        const generation = authGeneration.current;

        try {
            console.log('AuthContext: Checking auth...');
            const session = await fetchAuthSession();

            if (session?.tokens?.accessToken) {
                console.log('AuthContext: Valid session found');
                const currentUser = await getCurrentUser();
                const accessTokenStr = session.tokens.accessToken.toString();
                if (generation !== authGeneration.current) return;

                setUser({
                    userId: currentUser.userId,
                    username: currentUser.username,
                });
                setAuthState('authenticated');

                // Sync access token to useUserStore
                await syncTokenToStore(accessTokenStr, currentUser.userId, currentUser.username, generation);
            } else {
                if (generation !== authGeneration.current) return;
                useUserStore.getState().logout();
                console.log('AuthContext: No valid tokens');
                setUser(null);
                setAuthState('unauthenticated');
            }
        } catch (error) {
            if (generation !== authGeneration.current) return;
            useUserStore.getState().logout();
            console.log('AuthContext: No valid session', error);
            setUser(null);
            setAuthState('unauthenticated');
        } finally {
            authCheckComplete.current = true;
            checkingAuth.current = false;
        }
    }, [syncTokenToStore]);

    const logout = useCallback(async () => {
        authGeneration.current++;
        useUserStore.getState().logout();
        // Clear refresh interval
        if (tokenRefreshInterval.current) {
            clearInterval(tokenRefreshInterval.current);
            tokenRefreshInterval.current = null;
        }

        try {
            await signOut();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            setAuthState('unauthenticated');

            // Also clear useUserStore
            useUserStore.getState().logout();
        }
    }, []);

    // Initial auth check - only run once (skip if OAuth callback - let Hub events handle it)
    useEffect(() => {
        if (!authCheckComplete.current && !isOAuthCallback()) {
            const timer = setTimeout(() => {
                checkAuth();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [checkAuth]);

    // Set up periodic token refresh (every 50 minutes to stay ahead of 1 hour expiry)
    useEffect(() => {
        if (authState === 'authenticated') {
            // Refresh token every 50 minutes
            tokenRefreshInterval.current = setInterval(() => {
                console.log('AuthContext: Periodic token refresh');
                refreshToken();
            }, 50 * 60 * 1000); // 50 minutes

            return () => {
                if (tokenRefreshInterval.current) {
                    clearInterval(tokenRefreshInterval.current);
                }
            };
        }
    }, [authState, refreshToken]);

    // Listen for auth events
    useEffect(() => {
        const unsubscribe = Hub.listen('auth', async ({ payload }) => {
            console.log('Auth Hub event:', payload.event);

            switch (payload.event) {
                case 'signedIn':
                    // Check if this was an OAuth flow and redirect
                    if (isOAuthFlow.current && !hasRedirected.current) {
                        hasRedirected.current = true;
                        console.log('AuthContext: OAuth signIn complete, redirecting to dashboard');
                        window.location.href = '/dashboard';
                        return;
                    }
                    checkAuth();
                    break;
                case 'tokenRefresh':
                    console.log('AuthContext: Token was refreshed by Amplify');
                    checkAuth();
                    break;
                case 'tokenRefresh_failure':
                    authGeneration.current++;
                    useUserStore.getState().logout();
                    console.error('AuthContext: Token refresh failed');
                    // Token refresh failed - user needs to re-authenticate
                    setUser(null);
                    setAuthState('unauthenticated');
                    break;
                case 'signedOut':
                    authGeneration.current++;
                    useUserStore.getState().logout();
                    setUser(null);
                    setAuthState('unauthenticated');
                    break;
                case 'signInWithRedirect':
                    console.log('AuthContext: signInWithRedirect event received');
                    isOAuthFlow.current = true;
                    setAuthState('loading');
                    break;
                case 'signInWithRedirect_failure':
                    console.error('AuthContext: OAuth failed', payload.data);
                    isOAuthFlow.current = false;
                    setAuthState('unauthenticated');
                    break;
            }
        });

        return () => unsubscribe();
    }, [checkAuth]);

    // Derived state for backward compatibility
    const isLoading = authState === 'loading';
    const isAuthenticated = authState === 'authenticated';

    return (
        <AuthContext.Provider value={{
            user,
            authState,
            isLoading,
            isAuthenticated,
            checkAuth,
            refreshToken,
            logout
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
